import { createFileRoute, notFound } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { Container } from '../../components/ui/Container'
import { Button } from '../../components/ui/Button'
import { SafeImage } from '../../components/ui/SafeImage'
import BookGrid from '../../components/features/books/BookGrid'
import {
  ShoppingCart,
  Share2,
  Heart,
  Star,
  BookOpen,
  Calendar,
  Hash,
  FileText,
} from 'lucide-react'
import { formatPrice } from '../../lib/utils/format'
import { getProductBySlug, getRelatedProducts } from '../../lib/data/products'
import type { Product } from '@pranala/shared'

function generatePlaceholderDataURI(width: number, height: number): string {
  return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}'%3E%3Crect width='${width}' height='${height}' fill='%23e2e8f0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='14' fill='%2394a3b8'%3ENo Image%3C/text%3E%3C/svg%3E`
}

function generateWhatsAppLink(message: string): string {
  const phone = '6285124413535'
  return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
}

export const Route = createFileRoute('/shop/$slug')({
  component: BookDetail,
  loader: async ({ params }) => {
    const product = getProductBySlug(params.slug)
    if (!product) {
      throw notFound()
    }
    return { product }
  },
})

function BookDetail() {
  const { product } = Route.useLoaderData()
  const [selectedTab, setSelectedTab] = useState<'description' | 'details'>('description')
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([])

  // Get related products from static data
  useEffect(() => {
    const related = getRelatedProducts(product.slug, 4)
    setRelatedProducts(related)
  }, [product])

  // Transform product data
  const productPrice = product.price || 0
  const productSalePrice = product.salePrice
  const hasSalePrice = productSalePrice && productSalePrice < productPrice
  
  const book = {
    id: String(product.id),
    title: product.title,
    slug: product.slug,
    author: product.author || 'Unknown',
    displayPrice: hasSalePrice ? productSalePrice : productPrice,
    originalPrice: hasSalePrice ? productPrice : undefined,
    salePrice: hasSalePrice ? productSalePrice : undefined,
    coverImage: product.mediaCover?.url || product.coverImage || generatePlaceholderDataURI(400, 600),
    category: product.categories?.[0]?.category?.name || 'Umum',
    description: product.description || '',
    isbn: product.isbn || '-',
    publishedYear: product.publishedYear || new Date().getFullYear(),
    pages: product.pages || 0,
    language: product.language || 'Indonesia',
    publisher: product.publisher || 'Pustaka Pranala',
    weight: product.weight || 0,
    dimensions: product.dimensions || '-',
    rating: product.rating || 0,
    reviews: product.reviewCount || 0,
    stock: product.stockQuantity || 0,
  }

  // Use related products directly
  const relatedBooks = relatedProducts

  const handleWhatsAppOrder = () => {
    const message = `Halo, saya ingin memesan buku:\n\n*${book.title}*\nOleh: ${book.author}\nHarga: ${formatPrice(book.displayPrice)}\n\nMohon informasi cara pembayaran dan pengiriman. Terima kasih.`
    const whatsappUrl = generateWhatsAppLink(message)
    window.open(whatsappUrl, '_blank')
  }

  const handleWishlist = () => {
    alert('Fitur wishlist akan segera hadir!')
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: book.title,
        text: `${book.title} oleh ${book.author} - ${formatPrice(book.displayPrice)}`,
        url: window.location.href,
      })
    } else {
      navigator.clipboard.writeText(window.location.href)
      alert('Link buku telah disalin ke clipboard!')
    }
  }

  const discount =
    book.salePrice && book.originalPrice
      ? Math.round(((book.originalPrice - book.salePrice) / book.originalPrice) * 100)
      : 0

  return (
    <div className="min-h-screen bg-brand-slate-50">
      <Container className="py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Book Cover */}
          <div>
            <div className="sticky top-8">
              <div className="max-w-sm mx-auto lg:max-w-md">
                <div className="aspect-3/4 bg-brand-slate-100 rounded-xl overflow-hidden mb-6 border border-brand-slate-200">
                  <SafeImage
                    src={book.coverImage}
                    alt={book.title}
                    width={400}
                    height={600}
                    className="w-full h-full object-cover"
                    fallback="book"
                  />
                </div>
              </div>

              {/* Action Buttons - Mobile */}
              <div className="lg:hidden space-y-3">
                <Button
                  onClick={handleWhatsAppOrder}
                  className="w-full bg-yellow-500 hover:bg-yellow-400 text-white font-medium rounded-lg border-0"
                  size="lg"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Order via WhatsApp
                </Button>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="outline"
                    onClick={handleShare}
                    className="border-brand-slate-200 hover:border-brand-yellow-400 hover:text-brand-yellow-600"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Bagikan
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleWishlist}
                    className="border-brand-slate-200 hover:border-brand-yellow-400 hover:text-brand-yellow-600"
                  >
                    <Heart className="w-4 h-4 mr-2" />
                    Simpan
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Book Info */}
          <div className="space-y-6">
            {/* Title and Category */}
            <div>
              <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-md font-medium mb-3 inline-block">
                {book.category}
              </span>
              <h1 className="text-3xl lg:text-4xl text-brand-slate-900 font-bold tracking-tight mb-3">
                {book.title}
              </h1>
              <p className="text-lg text-brand-slate-600">oleh {book.author}</p>
            </div>

            {/* Rating and Reviews */}
            {book.rating > 0 && (
              <div className="flex items-center gap-4">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${
                        i < Math.floor(book.rating)
                          ? 'fill-brand-yellow-400 text-brand-yellow-400'
                          : 'text-brand-slate-300'
                      }`}
                    />
                  ))}
                  <span className="ml-2 font-semibold text-brand-slate-900">{book.rating}</span>
                </div>
                <span className="text-brand-slate-500">({book.reviews} ulasan)</span>
              </div>
            )}

            {/* Price */}
            <div className="flex items-center gap-3">
              <span className="text-3xl font-bold text-brand-slate-900">
                {formatPrice(book.displayPrice)}
              </span>
              {discount > 0 && book.originalPrice && (
                <>
                  <span className="text-lg text-brand-slate-400 line-through">
                    {formatPrice(book.originalPrice)}
                  </span>
                  <span className="px-2 py-0.5 bg-yellow-500 text-white text-xs rounded-md font-medium">
                    {discount}% OFF
                  </span>
                </>
              )}
            </div>

            {/* Stock Status */}
            <div className="flex items-center gap-2">
              <div
                className={`w-3 h-3 rounded-full ${book.stock > 0 ? 'bg-green-500' : 'bg-red-500'}`}
              />
              <span className={`text-sm ${book.stock > 0 ? 'text-green-700' : 'text-red-700'}`}>
                {book.stock > 0 ? `Tersedia (${book.stock} buku)` : 'Stok Habis'}
              </span>
            </div>

            {/* Quick Info */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 bg-white border border-brand-slate-200 rounded-xl">
              <div className="text-center">
                <FileText className="w-5 h-5 text-yellow-600 mx-auto mb-1" />
                <div className="text-xs text-brand-slate-500">Halaman</div>
                <div className="font-semibold text-brand-slate-900">{book.pages || '-'}</div>
              </div>
              <div className="text-center">
                <Calendar className="w-5 h-5 text-yellow-600 mx-auto mb-1" />
                <div className="text-xs text-brand-slate-500">Terbit</div>
                <div className="font-semibold text-brand-slate-900">{book.publishedYear}</div>
              </div>
              <div className="text-center">
                <BookOpen className="w-5 h-5 text-yellow-600 mx-auto mb-1" />
                <div className="text-xs text-brand-slate-500">Bahasa</div>
                <div className="font-semibold text-brand-slate-900">{book.language}</div>
              </div>
              <div className="text-center">
                <Hash className="w-5 h-5 text-yellow-600 mx-auto mb-1" />
                <div className="text-xs text-brand-slate-500">ISBN</div>
                <div className="font-semibold text-neutral-900 text-xs">{book.isbn}</div>
              </div>
            </div>

            {/* Tabs */}
            <div>
              <div className="border-b border-brand-slate-200">
                <nav className="-mb-px flex space-x-8">
                  {[
                    { key: 'description', label: 'Deskripsi' },
                    { key: 'details', label: 'Detail' },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setSelectedTab(tab.key as any)}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        selectedTab === tab.key
                          ? 'border-brand-yellow-500 text-brand-yellow-600'
                          : 'border-transparent text-brand-slate-500 hover:text-brand-slate-700 hover:border-brand-slate-300'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              <div className="py-6">
                {selectedTab === 'description' && (
                  <div className="space-y-4">
                    <p 
                      className="text-brand-slate-700 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: book.description || 'Tidak ada deskripsi' }}
                    />
                  </div>
                )}

                {selectedTab === 'details' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-brand-slate-500">Penerbit:</span>
                        <p className="font-medium text-brand-slate-900">{book.publisher}</p>
                      </div>
                      <div>
                        <span className="text-sm text-brand-slate-500">Bahasa:</span>
                        <p className="font-medium text-brand-slate-900">{book.language}</p>
                      </div>
                      <div>
                        <span className="text-sm text-brand-slate-500">Halaman:</span>
                        <p className="font-medium text-brand-slate-900">{book.pages || '-'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-brand-slate-500">Tahun Terbit:</span>
                        <p className="font-medium text-brand-slate-900">{book.publishedYear}</p>
                      </div>
                      <div>
                        <span className="text-sm text-brand-slate-500">Berat:</span>
                        <p className="font-medium text-brand-slate-900">
                          {book.weight ? `${book.weight} gram` : '-'}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-brand-slate-500">Ukuran:</span>
                        <p className="font-medium text-brand-slate-900">{book.dimensions}</p>
                      </div>
                      <div className="col-span-2">
                        <span className="text-sm text-brand-slate-500">ISBN:</span>
                        <p className="font-medium text-brand-slate-900">{book.isbn}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons - Desktop */}
            <div className="hidden lg:block space-y-4">
              <Button
                onClick={handleWhatsAppOrder}
                className="w-full bg-yellow-500 hover:bg-yellow-400 text-white font-medium rounded-lg border-0"
                size="lg"
                disabled={book.stock === 0}
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                {book.stock > 0 ? 'Order via WhatsApp' : 'Stok Habis'}
              </Button>
              <div className="grid grid-cols-2 gap-4">
                <Button
                  variant="outline"
                  onClick={handleShare}
                  className="border-brand-slate-200 hover:border-brand-yellow-400 hover:text-brand-yellow-600"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Bagikan
                </Button>
                <Button
                  variant="outline"
                  onClick={handleWishlist}
                  className="border-brand-slate-200 hover:border-brand-yellow-400 hover:text-brand-yellow-600"
                >
                  <Heart className="w-4 h-4 mr-2" />
                  Simpan
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Related Books */}
        {relatedBooks.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl text-brand-slate-900 font-bold tracking-tight mb-8">
              Buku Terkait
            </h2>
            <BookGrid books={relatedBooks} />
          </div>
        )}
      </Container>
    </div>
  )
}
